import React, { useState, useEffect, useCallback } from 'react';
import { FormattedMessage, FormattedDate, useIntl, FormattedPlural } from 'react-intl';
import { Button, Checkbox, Row, Col, Popconfirm, Collapse, Modal, List } from 'antd';
import {
  CloudDownloadOutlined,
  DeleteOutlined,
  ReloadOutlined,
  PlusCircleOutlined,
  RocketOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import Loader from '../Loader';
import { FormatUtils } from '../../../utils';
import { trackEvent } from '../../../controllers/TrackingController';
import { useGetMe, useSetMe } from '../../hooks/query/user';
import { useGetVideos, useCreateVideo, useDeleteVideo } from '../../hooks/query/headliner';
import { useGetShow } from '../../hooks/query/show';
import { useGetFeatures } from '../../hooks/query/features';
import UpgradeModal from './UpgradeModal';
import GrandfatherNotice from '../features/GrandfatherNotice';
import useMessage from '../../hooks/useMessage';

const HeadlinerIntegrationModal = () => {
  const intl = useIntl();
  const { message } = useMessage();
  const { showId, episodeId } = useParams();
  const { user, isUserLoading } = useGetMe();
  const { setUser, isSetUserLoading } = useSetMe();
  const { show, isShowLoading } = useGetShow();

  const {
    headlinerVideos,
    isHeadlinerVideosLoading,
    checkHeadlinerVideos,
    isHeadlinerVideosFetching,
  } = useGetVideos();
  const { createVideo, isCreateVideoLoading, headlinerVideoUrl } = useCreateVideo();
  const { deleteVideo, isDeleteVideoLoading } = useDeleteVideo();
  const { featuresPermissions, isFeaturesPermissionsLoading } = useGetFeatures();
  const [modalVisible, setModalVisible] = useState(false);
  const [headlinerUpgradeModalVisible, setHeadlinerUpgradeModalVisible] = useState(false);

  const isLoading = isUserLoading || isShowLoading || isFeaturesPermissionsLoading;
  const isHeadlinerEnabled = user?.integrations?.headliner?.isEnabled;
  const isHeadlinerActive = !!headlinerVideos?.length;
  const isHeadlinerAvailable = !!(show?.createImport || featuresPermissions?.canUseHeadliner);
  const isGrandfatherNotice = isHeadlinerEnabled && isHeadlinerActive && !isHeadlinerAvailable;
  const isUpgradeAvailable = (!isHeadlinerEnabled || !isHeadlinerActive) && !isHeadlinerAvailable;

  useEffect(() => {
    // We auto create a video link to add it to the create button
    if (!isCreateVideoLoading && modalVisible && !headlinerVideoUrl && isHeadlinerAvailable) {
      createVideo({ path: { showId, episodeId } });
    }
  }, [
    createVideo,
    isCreateVideoLoading,
    modalVisible,
    headlinerVideoUrl,
    showId,
    episodeId,
    isHeadlinerAvailable,
  ]);

  const handleDeleteVideo = useCallback(
    async (videoId) => {
      deleteVideo(
        { path: { showId, episodeId, videoId } },
        {
          onSuccess: () => {
            message.success({
              content: <FormattedMessage defaultMessage="Your video was deleted" />,
            });
          },
          onError: () => {
            message.danger({
              content: <FormattedMessage defaultMessage="There was an error deleting your video" />,
            });
          },
        }
      );
    },
    [deleteVideo, episodeId, message, showId]
  );

  const onCheckboxChange = useCallback(
    (e) => {
      const checked = e.target.checked;
      if (checked) {
        setUser({
          data: {
            integrations: {
              headliner: { isEnabled: true },
            },
          },
        });
      }
    },
    [setUser]
  );

  return (
    <>
      <div>
        <Button
          icon={
            isHeadlinerVideosLoading ? null : isHeadlinerAvailable ? (
              <RocketOutlined />
            ) : (
              <StarOutlined />
            )
          }
          onClick={() => {
            if (isUpgradeAvailable) {
              setHeadlinerUpgradeModalVisible(true);
            } else {
              setModalVisible(true);
              trackEvent({ category: 'action-links', action: 'click', label: 'headliner' });
            }
          }}
        >
          <FormattedPlural
            value={headlinerVideos?.length}
            one={<FormattedMessage defaultMessage="Video" />}
            other={<FormattedMessage defaultMessage="Videos" />}
          />
        </Button>
      </div>

      <UpgradeModal
        headlinerUpgradeModalVisible={headlinerUpgradeModalVisible}
        setHeadlinerUpgradeModalVisible={setHeadlinerUpgradeModalVisible}
      />

      <Modal
        title={<FormattedMessage defaultMessage="Grow your audience" />}
        footer={null}
        width={1020}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
      >
        {!isHeadlinerEnabled ? (
          <Row gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}>
            <Col sm={24}>
              <Row justify="space-between">
                <div>
                  <h3>
                    <FormattedMessage defaultMessage="Create and publish a stunning audio visualization for your social channels with Headliner" />
                  </h3>
                  <p>
                    <FormattedMessage defaultMessage="Headliner is a free tool that allows you to create and publish a stunning audio visualization for your social channels." />
                  </p>

                  {!isLoading && (
                    <Row gutter={8}>
                      <Col>
                        {isSetUserLoading ? <Loader /> : <Checkbox onChange={onCheckboxChange} />}
                      </Col>
                      <Col>
                        <span className="text-grey--dark">
                          <FormattedMessage
                            defaultMessage="I agree to the <link>Terms & Conditions</link>"
                            values={{
                              link: function renderLink(chunk) {
                                return (
                                  <a
                                    href="https://static.sparemin.com/static/doc/201808291932/terms-of-service.html"
                                    rel="noopener noreferrer"
                                    target="_blank"
                                  >
                                    {chunk}
                                  </a>
                                );
                              },
                            }}
                          />
                        </span>
                      </Col>
                    </Row>
                  )}
                </div>
              </Row>
            </Col>
          </Row>
        ) : (
          <>
            {isGrandfatherNotice && <GrandfatherNotice />}
            <List
              className="headeliner-list"
              loadMore={
                <Row justify="center" className="headeliner-list--button">
                  <Button
                    loading={isHeadlinerVideosFetching}
                    onClick={checkHeadlinerVideos}
                    icon={<ReloadOutlined />}
                    type="link"
                  >
                    <FormattedMessage defaultMessage="Refresh" />
                  </Button>
                </Row>
              }
              header={
                <Row gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }} justify="space-between">
                  <Col>
                    <h3>
                      <FormattedPlural
                        value={headlinerVideos?.length}
                        one={<FormattedMessage defaultMessage="Your video" />}
                        other={<FormattedMessage defaultMessage="Your videos" />}
                      />{' '}
                    </h3>
                  </Col>
                  <Col>
                    <Button
                      onClick={() => {
                        createVideo({ path: { showId, episodeId } });
                        trackEvent({
                          category: 'action-links',
                          action: 'create-video',
                          label: 'promo-videos',
                        });
                      }}
                      icon={<PlusCircleOutlined />}
                      disabled={
                        isHeadlinerVideosLoading ||
                        isHeadlinerVideosFetching ||
                        !headlinerVideoUrl ||
                        !isHeadlinerAvailable
                      }
                      loading={isCreateVideoLoading}
                      href={headlinerVideoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      type="primary"
                    >
                      <FormattedMessage defaultMessage="Create a new video" />
                    </Button>
                  </Col>
                </Row>
              }
              loading={isHeadlinerVideosLoading || isHeadlinerVideosFetching}
              itemLayout="vertical"
              size="large"
              dataSource={headlinerVideos?.sort((a, b) => {
                return new Date(b.creationDate) - new Date(a.creationDate);
              })}
              renderItem={(video) => (
                <>
                  <List.Item
                    key={video._id}
                    actions={[
                      <Popconfirm
                        key="delete"
                        title={intl.formatMessage({
                          defaultMessage: 'Are you sure to delete this video?',
                        })}
                        onConfirm={() => handleDeleteVideo(video._id)}
                        okText={intl.formatMessage({
                          defaultMessage: 'Delete',
                        })}
                        cancelText={intl.formatMessage({
                          defaultMessage: 'Cancel',
                        })}
                      >
                        <Button
                          danger
                          type="primary"
                          loading={isDeleteVideoLoading}
                          icon={<DeleteOutlined />}
                        >
                          <FormattedMessage defaultMessage="Delete" />
                        </Button>
                      </Popconfirm>,

                      <Button
                        type="primary"
                        key="download"
                        href={video.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <CloudDownloadOutlined /> <FormattedMessage defaultMessage="Download" />
                      </Button>,
                    ]}
                    extra={<video src={video.url} controls style={{ maxHeight: '200px' }} />}
                  >
                    <List.Item.Meta
                      title={video.name}
                      description={
                        <ul>
                          <li>
                            <b>
                              <FormattedMessage
                                defaultMessage="Creation date"
                                values={{ date: video.creationDate }}
                              />{' '}
                              :
                            </b>{' '}
                            <FormattedDate
                              value={video.creationDate}
                              year="numeric"
                              month="long"
                              day="2-digit"
                              hour="numeric"
                              minute="numeric"
                            />
                          </li>
                          <li>
                            <b>
                              <FormattedMessage defaultMessage="Format" /> :
                            </b>{' '}
                            {video.width}x{video.height}
                          </li>
                          <li>
                            <b>
                              <FormattedMessage defaultMessage="Duration" /> :
                            </b>{' '}
                            {FormatUtils.formatTimeWholeSeconds(video.duration)}
                          </li>
                        </ul>
                      }
                    />
                  </List.Item>
                </>
              )}
            />

            <Collapse className="collapse-headliner-terms">
              <Collapse.Panel header={<FormattedMessage defaultMessage="Terms & Conditions" />}>
                <p>
                  <FormattedMessage defaultMessage="Create and publish a stunning audio visualization for your social channels with Headliner" />
                </p>
                <p>
                  <Checkbox disabled checked>
                    <FormattedMessage
                      defaultMessage="I agree to the <link>Terms & Conditions</link>"
                      values={{
                        link: function renderLink(chunk) {
                          return (
                            <a
                              href="https://static.sparemin.com/static/doc/201808291932/terms-of-service.html"
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              {chunk}
                            </a>
                          );
                        },
                      }}
                    />
                  </Checkbox>
                </p>
              </Collapse.Panel>
            </Collapse>
          </>
        )}
      </Modal>
    </>
  );
};

export default HeadlinerIntegrationModal;
